import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Home as HomeIcon, User as UserIcon, Heart as HeartIcon, Star as StarIcon, Users as UsersIcon, Eye as EyeIcon, Mail as MailIcon, Phone as PhoneIcon, Sun as SunIcon, Bot as BotIcon, Store as StoreIcon, Diamond as DiamondIcon, Settings as SettingsIcon, HelpCircle as HelpCircleIcon, Shield as ShieldIcon, LogOut as LogOutIcon, Camera as CameraIcon, Lock as LockIconLucide } from 'lucide-react';
import {
  FaHome, FaHeart, FaSearch, FaStar, FaEye, FaUserFriends, FaEnvelope, FaPhone, FaStore,
  FaSun, FaMoon, FaGem, FaCog, FaQuestionCircle, FaLock, FaSignOutAlt, FaBell, FaBars, FaFilter,
  FaUser, FaRobot, FaCheckCircle, FaCamera, FaCircle, FaGift, FaTrash, FaTimes
} from 'react-icons/fa';
import Home from '../components/dashboard/Home';
import Matches from '../components/dashboard/Matches';
import Search from '../components/dashboard/Search';
import Offers from '../components/dashboard/Offers';
import Messages from '../components/dashboard/Messages';
import Subscription from '../components/dashboard/Subscription';
import Settings from '../components/dashboard/Settings';
import Profile from '../components/dashboard/Profile';
import ProfileDetails from '../components/dashboard/ProfileDetails';
import Shortlisted from '../components/dashboard/Shortlisted';
import ViewedProfiles from '../components/dashboard/ViewedProfiles';
import Visitors from '../components/dashboard/Visitors';
import ContactReveals from '../components/dashboard/ContactReveals';
import HoroscopeMatch from '../components/dashboard/HoroscopeMatch';
// import Events from '../components/dashboard/Events';
import Help from '../components/dashboard/Help';
import DashboardPrivacy from '../components/dashboard/DashboardPrivacy';
import Market from '../components/dashboard/Market';
import AIJathagam from '../components/dashboard/AIJathagam';
import api from '../services/api';
import useSocket from "../components/dashboard/socketService";

const Dashboard = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tab || 'home');

  useEffect(() => {
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    } else if (!tab && activeTab !== 'home') {
      setActiveTab('home');
    }
  }, [tab]);

  useEffect(() => {
    navigate(`/dashboard${activeTab === 'home' ? '' : '/' + activeTab}`, { replace: true });
  }, [activeTab, navigate]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [conversationToOpenId, setConversationToOpenId] = useState(null);
  const [features, setFeatures] = useState({});
  const [filter, setFilter] = useState("all");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return false; // Default to light mode
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const isMessagesScreenActiveRef = useRef(false);


  // Profile Data State
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [stats, setStats] = useState({
    profileViews: 0,
    shortlisted: 0,
    matches: 0,
    messages: 0
  });

  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [searchFilters, setSearchFilters] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = storedUser?._id || storedUser?.id || null;
  // NEW: Unread message count for Chat icon badge
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // GLOBAL NOTIFICATION SOUND REF
  const notificationAudioRef = useRef(null);
  // Track which notifications already played sound
  const notifiedIdsRef = useRef(new Set());
  const { forceDisconnect } = useSocket(userId); // or get from context if needed

  const audioUnlockedRef = useRef(false);
  const activeConversationRef = useRef(null);

  const unlockAudio = () => {
    if (audioUnlockedRef.current) return;

    const audio = notificationAudioRef.current;
    if (!audio) return;

    audio.muted = true;      // 🔕 mute first
    audio.volume = 0;
    audio.play().then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;  // 🔊 unmute AFTER unlock
      audio.volume = 1;
      audioUnlockedRef.current = true;
      console.log("🔓 Audio unlocked silently");
    }).catch(() => { });

    document.removeEventListener("click", unlockAudio);
    document.removeEventListener("keydown", unlockAudio);
    document.removeEventListener("touchstart", unlockAudio);
  };


  // GLOBAL SOCKET FOR NOTIFICATION SOUND ONLY
  useSocket(userId, {
    onNewMessage: ({ conversationId, message }) => {
      const isOwnMessage = String(message.sender) === String(userId);
      if (isOwnMessage) return;

      // 🚫 Messages screen is active → NEVER play sound
      if (isMessagesScreenActiveRef.current) {
        return;
      }

      // ✅ Play sound only when user is outside chat screen
      if (audioUnlockedRef.current && notificationAudioRef.current) {
        const audio = notificationAudioRef.current;
        audio.pause();
        audio.currentTime = 0;
        audio.play().catch(() => { });
      }

      setUnreadMessageCount(prev => prev + 1);
    },



    onNewNotification: (notification) => {
      // 🔔 ensure unread
      notification.isRead = false;

      // 🔊 play sound ONLY if audio unlocked and not already played
      if (!notifiedIdsRef.current.has(notification._id)) {
        notifiedIdsRef.current.add(notification._id);

        if (audioUnlockedRef.current && notificationAudioRef.current) {
          const audio = notificationAudioRef.current;
          audio.pause();
          audio.currentTime = 0;
          audio.play().catch(() => { });
        }
      }

      setNotifications(prev => {
        if (prev.some(n => n._id === notification._id)) return prev;
        return [notification, ...prev];
      });
    }
  });

  useEffect(() => {
    const handler = (e) => {
      activeConversationRef.current = e.detail || null;
    };



    window.addEventListener("active-conversation", handler);
    return () => window.removeEventListener("active-conversation", handler);
  }, []);


  useEffect(() => {
    const unlockAudioHandler = () => unlockAudio();

    document.addEventListener("click", unlockAudioHandler);
    document.addEventListener("keydown", unlockAudioHandler);
    document.addEventListener("touchstart", unlockAudioHandler);

    return () => {
      document.removeEventListener("click", unlockAudioHandler);
      document.removeEventListener("keydown", unlockAudioHandler);
      document.removeEventListener("touchstart", unlockAudioHandler);
    };
  }, [unreadMessageCount, notifications]);



  useEffect(() => {
    fetchProfileData();
    fetchNotifications();
  }, []);

  useEffect(() => {
    isMessagesScreenActiveRef.current = activeTab === "messages";
  }, [activeTab]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the notification dropdown
      const notificationDropdown = document.querySelector('.notification-dropdown-container');
      const notificationButton = document.querySelector('.notification-bell-button');

      if (
        notificationOpen &&
        notificationDropdown &&
        !notificationDropdown.contains(event.target) &&
        notificationButton &&
        !notificationButton.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (notificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup: remove listener when dropdown closes or component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationOpen]); // Only re-run if notificationOpen changes

  const fetchProfileData = async () => {
    try {
      const response = await api.get('/dashboard/my-profile');
      setUser(response.data.user);
      setProfile(response.data.profile);
      setFeatures(response.data.subscription?.planDetails?.features || {});
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response && (error.response.status === 404 || error.response.status === 401)) {
        handleLogout();
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get("/getall");
      setNotifications(res.data.data || []);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching notifications", err);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await api.delete("/clear-all");

      setNotifications([]);
      notifiedIdsRef.current.clear();
      setNotificationOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/${notificationId}/read`);

      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, isRead: true } : n
        )
      );
    } catch (error) {
      console.error("Error marking notification as read", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post("/mark-all-read");

      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    window.dispatchEvent(new Event("app-logout"));
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    forceDisconnect?.();

    navigate("/login");
  };
  const handleViewProfile = (profileId) => {
    setSelectedProfileId(profileId);
    setActiveTab('view-profile');
  };


  const handleSearch = (filters) => {
    setFilter('search');          // 🔑 THIS IS REQUIRED
    setSearchFilters(filters);
    setActiveTab('search_results');
  };

  const openConversationAndMessages = (conversationId = null) => {
    activeConversationRef.current = conversationId;
    setConversationToOpenId(conversationId);
    setActiveTab('messages');
  };

  const FEATURE_TABS = {
    horoscope: "horoscopeMatching",
    "ai-jathagam": "aiJathagam",
    market: "marketAccess",
    messages: "chatAccess",
  };
  const handleFeatureClick = (tab) => {
    const requiredFeature = FEATURE_TABS[tab];

    if (requiredFeature && !features[requiredFeature]) {
      setActiveTab("subscription"); // 🔥 redirect
      return;
    }

    setActiveTab(tab);
    setSidebarOpen(false);
  };


  const renderContent = () => {
    switch (activeTab) {
      case 'home': return <Home setActiveTab={setActiveTab} />;
      case 'matches':
        return (
          <Matches
            filter={filter}
            searchQuery={searchQuery}
            onViewProfile={handleViewProfile}
            onOpenConversation={openConversationAndMessages}
            onUpgradeClick={() => setActiveTab('subscription')}
          />
        );
      case 'search_results':
        return (
          <Matches
            filter={filter}
            searchFilters={searchFilters}
            onViewProfile={handleViewProfile}
            onOpenConversation={openConversationAndMessages}
          />
        );
      case 'offers': return <Offers onBack={() => setActiveTab('home')} />;
      case 'view-profile':
        return (
          <ProfileDetails
            profileId={selectedProfileId}
            onBack={() => setActiveTab('matches')}
            onOpenConversation={openConversationAndMessages}
            onUpgradeClick={() => setActiveTab('subscription')}
          />
        );
      case 'search': return <Search onSearch={handleSearch} />;
      case 'shortlisted': return <Shortlisted onViewProfile={handleViewProfile} />;
      case 'viewed': return <ViewedProfiles onViewProfile={handleViewProfile} />;
      case 'visitors':
        return <Visitors setActiveTab={setActiveTab} />;
      case 'messages': return <Messages conversationToOpenId={conversationToOpenId} setActiveTab={setActiveTab} />;
      case 'contacts': return <ContactReveals />;
      case 'horoscope': return <HoroscopeMatch />;
      case 'ai-jathagam': return <AIJathagam />;
      case 'market': return <Market />;
      case 'subscription': return <Subscription />;
      case 'settings': return <Settings />;
      case 'profile': return <Profile onProfileUpdate={fetchProfileData} />;
      case 'help': return <Help />;
      case 'privacy': return <DashboardPrivacy />;
      case 'notifications': return <NotificationsPage notifications={notifications} onClearAll={clearAllNotifications} onMarkAllRead={markAllAsRead} />;
      default: return <Matches filter="all" searchQuery={searchQuery} onViewProfile={handleViewProfile} onOpenConversation={openConversationAndMessages} />;
    }
  };

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const displayCount = unreadCount > 10 ? '10+' : unreadCount;
  const SIDEBAR_ITEMS = [
    { key: "home", label: "Home" },
    { key: "profile", label: "My Profile" },
    { key: "matches", label: "Matches" },
    { key: "shortlisted", label: "Shortlisted" },
    { key: "visitors", label: "Visitors" },
    { key: "viewed", label: "Viewed Profiles" },
    { key: "messages", label: "Chat", feature: "chatAccess" },
    { key: "contacts", label: "Contact Reveals" },
    { key: "horoscope", label: "Horoscope Match", feature: "horoscopeMatching" },
    { key: "ai-jathagam", label: "AI Jathagam", feature: "aiJathagam" },
    { key: "market", label: "Market", feature: "marketAccess" },
    { key: "subscription", label: "Membership Plans" },
    { key: "settings", label: "Settings" },
    { key: "help", label: "Help & Support" },
    { key: "privacy", label: "Privacy Policy" },
  ];
  const filteredSuggestions = SIDEBAR_ITEMS.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getIconForItem = (key) => {
    const iconMap = {
      home: <HomeIcon className="w-5 h-5" />,
      profile: <UserIcon className="w-5 h-5" />,
      matches: <HeartIcon className="w-5 h-5" />,
      shortlisted: <StarIcon className="w-5 h-5" />,
      visitors: <UsersIcon className="w-5 h-5" />,
      viewed: <EyeIcon className="w-5 h-5" />,
      messages: <MailIcon className="w-5 h-5" />,
      contacts: <PhoneIcon className="w-5 h-5" />,
      horoscope: <SunIcon className="w-5 h-5" />,
      'ai-jathagam': <BotIcon className="w-5 h-5" />,
      market: <StoreIcon className="w-5 h-5" />,
      subscription: <DiamondIcon className="w-5 h-5" />,
      settings: <SettingsIcon className="w-5 h-5" />,
      help: <HelpCircleIcon className="w-5 h-5" />,
      privacy: <ShieldIcon className="w-5 h-5" />
    };
    return iconMap[key] || <div className="w-5 h-5 rounded-full border border-current" />;
  };
  return (
    <div className="flex h-screen bg-dark-navy font-sans text-text-secondary overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-grad-end/10 via-dark-navy to-dark-navy pointer-events-none" />
      {/* ADDED: A nice soft chime notification sound (royalty-free) */}
      <audio ref={notificationAudioRef} src="/notification.mp3" preload="auto" />

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      {/* Sidebar - Premium SaaS Redesign */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[280px] bg-dark-navy border-r border-gray-200 dark:border-glass-border transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:relative lg:translate-x-0 flex flex-col h-full ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Glassmorphism Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary-pink/10 rounded-full blur-[60px] pointer-events-none" />
        <div className="absolute bottom-1/2 left-0 w-40 h-40 bg-primary-grad-end/10 rounded-full blur-[80px] pointer-events-none" />

        {/* Sidebar Header */}
        <div className="relative px-6 h-[72px] flex items-center justify-center gap-3 border-b border-gray-200 dark:border-glass-border bg-transparent shrink-0 z-10">
          {/* Brand Name */}
          <h1 className="text-[26px] font-bold tracking-wide flex items-center">
            <span className="text-text-primary dark:text-white mr-1.5">Kalyana</span>
            <span className="text-primary-pink">Matrimony</span>
          </h1>
        </div>

        {/* Scrollable Nav Area */}
        <div className="relative flex-1 overflow-y-auto custom-scrollbar z-10 scroll-smooth">



          <nav className="p-4">
            <div className="space-y-1">
              <SidebarItem
                icon={<HomeIcon className="w-5 h-5" />}
                label="Home"
                active={activeTab === "home"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("home");
                  setSidebarOpen(false);
                }}
              />
              <SidebarItem
                icon={<UserIcon className="w-5 h-5" />}
                label="My Profile"
                active={activeTab === "profile"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("profile");
                  setSidebarOpen(false);
                }}
              />
              <SidebarItem
                icon={<HeartIcon className="w-5 h-5" />}
                label="Matches"
                active={activeTab === "matches"}
                onClick={() => {
                  unlockAudio();
                  setFilter("all");
                  setSearchFilters(null);
                  setActiveTab("matches");
                  setSidebarOpen(false);
                }}
              />
              <SidebarItem
                icon={<StarIcon className="w-5 h-5" />}
                label="Shortlisted"
                active={activeTab === "shortlisted"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("shortlisted");
                  setSidebarOpen(false);
                }}
              />
              <SidebarItem
                icon={<UsersIcon className="w-5 h-5" />}
                label="Visitors"
                active={activeTab === "visitors"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("visitors");
                  setSidebarOpen(false);
                }}
                badge="New"
              />
              <SidebarItem
                icon={<EyeIcon className="w-5 h-5" />}
                label="Viewed Profiles"
                active={activeTab === "viewed"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("viewed");
                  setSidebarOpen(false);
                }}
              />
              <SidebarItem
                icon={<MailIcon className="w-5 h-5" />}
                label="Chat"
                active={activeTab === "messages"}
                locked={!features?.chatAccess}
                onClick={() => {
                  unlockAudio();
                  handleFeatureClick("messages");
                }}
              />
              <SidebarItem
                icon={<PhoneIcon className="w-5 h-5" />}
                label="Contact Reveals"
                active={activeTab === "contacts"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("contacts");
                  setSidebarOpen(false);
                }}
              />
            </div>

            <div className="mt-6 mb-2 px-4">
              <h4 className="text-[10px] uppercase font-bold tracking-widest text-text-muted">Premium Tools</h4>
            </div>

            <div className="space-y-1">
              <SidebarItem
                icon={<SunIcon className="w-5 h-5" />}
                label="Horoscope Match"
                active={activeTab === "horoscope"}
                locked={!features?.horoscopeMatching}
                onClick={() => {
                  unlockAudio();
                  handleFeatureClick("horoscope");
                }}
              />
              <SidebarItem
                icon={<DiamondIcon className="w-5 h-5" />}
                label="Membership Plans"
                active={activeTab === "subscription"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("subscription");
                  setSidebarOpen(false);
                }}
              />
            </div>

            <div className="mt-6 mb-2 px-4">
              <h4 className="text-[10px] uppercase font-bold tracking-widest text-text-muted">System</h4>
            </div>

            <div className="space-y-1">
              <SidebarItem
                icon={<SettingsIcon className="w-5 h-5" />}
                label="Settings"
                active={activeTab === "settings"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("settings");
                  setSidebarOpen(false);
                }}
              />
              <SidebarItem
                icon={<HelpCircleIcon className="w-5 h-5" />}
                label="Help & Support"
                active={activeTab === "help"}
                onClick={() => {
                  unlockAudio();
                  setActiveTab("help");
                  setSidebarOpen(false);
                }}
              />
            </div>
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="relative p-4 border-t border-glass-border bg-transparent shrink-0 z-10">
          <button
            onClick={handleLogout}
            className="group flex items-center justify-center gap-3 w-full px-4 py-3 rounded-xl font-bold text-text-muted bg-glass-card hover:bg-black/5 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white border border-glass-border transition-all duration-300 shadow-sm"
          >
            <LogOutIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-transparent dark:bg-glass-card backdrop-blur-xl border-b border-gray-200 dark:border-glass-border z-20 shrink-0">
          <div className="flex items-center justify-between px-4 lg:px-8 h-[72px]">
            <div className="flex items-center flex-1">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden mr-4 p-2 text-text-secondary hover:bg-[rgba(255,255,255,0.1)] rounded-lg focus:outline-none"
              >
                <FaBars className="w-6 h-6" />
              </button>

              {/* Search Bar with Offers on Right + Clear Button */}
              <div className="relative flex items-center flex-1 max-w-3xl mx-auto hidden md:flex">
                <div className="relative flex-1 max-w-md">
                  <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none text-sm" />
                  <input
                    type="text"
                    placeholder="Search by ID, Name or Keyword..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                    className="w-full pl-10 pr-24 py-2.5 bg-[rgba(255,255,255,0.05)] border border-glass-border rounded-full focus:ring-2 focus:ring-primary-pink focus:bg-[rgba(255,255,255,0.1)] outline-none transition-all placeholder:text-text-muted text-sm text-text-primary shadow-[inset_0_2px_10px_rgba(0,0,0,0.2)]"
                  />

                  {/* Search meta info */}
                  {searchQuery && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-text-muted">
                      <span className="bg-[rgba(255,255,255,0.1)] px-2 py-0.5 rounded-full shadow-sm border border-glass-border text-text-primary">
                        {filteredSuggestions.length} results
                      </span>
                      <button onClick={() => setSearchQuery('')} className="p-1 hover:bg-[rgba(255,255,255,0.1)] rounded-full transition-colors">
                        <FaTimes className="text-xs" />
                      </button>
                    </div>
                  )}

                  {/* Keyboard shortcut hint */}
                  {!searchQuery && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:block">
                      <kbd className="px-2 py-1 text-xs font-semibold text-text-muted bg-[rgba(255,255,255,0.05)] rounded-md border border-glass-border shadow-sm">
                        ⌘ K
                      </kbd>
                    </div>
                  )}

                  {/* Enhanced Suggestions Dropdown */}
                  {searchQuery && filteredSuggestions.length > 0 && (
                    <div className="absolute mt-2 w-full bg-dark-navy/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.5)] rounded-xl border border-glass-border z-50 max-h-80 overflow-y-auto animate-fadeIn custom-scrollbar">
                      <div className="sticky top-0 bg-transparent px-4 py-2 border-b border-glass-border flex items-center justify-between text-xs text-text-muted">
                        <span>Quick Navigation</span>
                        <span className="flex items-center gap-1">
                          <kbd className="px-1.5 py-0.5 bg-[rgba(255,255,255,0.1)] rounded text-text-secondary text-xs">↑</kbd>
                          <kbd className="px-1.5 py-0.5 bg-[rgba(255,255,255,0.1)] rounded text-text-secondary text-xs">↓</kbd>
                          <span>to navigate</span>
                        </span>
                      </div>
                      <div className="py-1">
                        {filteredSuggestions.map((item, index) => {
                          const isLocked = item.feature && !features?.[item.feature];
                          return (
                            <div key={item.key} onClick={() => { if (!isLocked) { setActiveTab(item.key); setSearchQuery(""); } }} className={`px-4 py-3 flex items-center justify-between cursor-pointer transition-all duration-150 group ${isLocked ? 'text-text-muted bg-[rgba(255,255,255,0.02)]' : 'hover:bg-[rgba(255,46,136,0.1)]'} ${index === 0 && !isLocked ? 'border-l-2 border-primary-pink bg-[rgba(255,46,136,0.05)]' : ''}`}>
                              <div className="flex items-center gap-3">
                                <span className={`text-base transition-colors duration-150 ${isLocked ? 'text-text-muted' : 'text-text-muted group-hover:text-primary-pink'}`}>
                                  {item.icon || getIconForItem(item.key)}
                                </span>
                                <div>
                                  <span className="font-medium text-text-primary">{item.label}</span>
                                  {item.label.toLowerCase().includes(searchQuery.toLowerCase()) && (
                                    <span className="ml-2 text-xs text-primary-pink">matches search</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isLocked ? (
                                  <span className="flex items-center gap-1 text-xs bg-[rgba(255,215,0,0.1)] text-premium-gold px-2 py-1 rounded-full border border-[rgba(255,215,0,0.3)]">
                                    <FaLock className="text-[10px]" /> Premium
                                  </span>
                                ) : (
                                  <span className="text-xs text-text-muted opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                    <kbd className="px-1.5 py-0.5 bg-[rgba(255,255,255,0.1)] rounded text-text-secondary text-[10px]">↵</kbd>
                                    <span>Go to</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="sticky bottom-0 bg-dark-navy/95 backdrop-blur-md px-4 py-2 border-t border-glass-border flex items-center justify-between text-xs text-text-muted">
                        <button onClick={() => { setActiveTab('search'); setSearchQuery(''); }} className="flex items-center gap-2 text-primary-pink hover:text-rose-400 transition-colors group">
                          <FaSearch className="text-xs group-hover:scale-110 transition-transform" />
                          <span>Advanced Search</span>
                        </button>
                        <span className="text-text-muted">{filteredSuggestions.length} sections</span>
                      </div>
                    </div>
                  )}

                  {/* No results state */}
                  {searchQuery && filteredSuggestions.length === 0 && (
                    <div className="absolute mt-2 w-full bg-dark-navy/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.5)] rounded-xl border border-glass-border z-50 p-8 text-center animate-fadeIn">
                      <FaSearch className="text-4xl text-text-muted mx-auto mb-3" />
                      <p className="text-text-primary font-medium mb-1">No results found</p>
                      <p className="text-sm text-text-muted mb-4">Try searching with different keywords</p>
                      <button onClick={() => { setActiveTab('search'); setSearchQuery(''); }} className="text-sm text-primary-pink hover:text-rose-400 font-medium transition-colors">
                        Use advanced search →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 lg:space-x-6">
              {/* Theme Toggle Button */}
              <button onClick={toggleTheme} className="text-text-secondary hover:text-primary-pink transition-colors p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]" title="Toggle Theme">
                {isDarkMode ? <FaSun className="text-xl text-premium-gold" /> : <FaMoon className="text-xl text-primary-pink" />}
              </button>

              <button onClick={() => { unlockAudio(); setActiveTab("search"); }} className="text-text-secondary hover:text-primary-pink transition-colors p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]" title="Filter Profiles">
                <FaFilter className="text-xl" />
              </button>

              <button onClick={() => { unlockAudio(); setActiveTab("search"); }} className="md:hidden text-text-secondary hover:text-primary-pink transition-colors p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]">
                <FaSearch className="text-xl" />
              </button>

              <button onClick={() => { unlockAudio(); setActiveTab("shortlisted"); }} className="text-text-secondary hover:text-primary-pink transition-colors relative p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]" title="Shortlisted Profiles">
                <FaHeart className="text-xl" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary-pink rounded-full ring-2 ring-dark-navy"></span>
              </button>

              <button onClick={() => { unlockAudio(); setActiveTab("messages"); setUnreadMessageCount(0); }} className="text-text-secondary hover:text-primary-pink transition-colors relative p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]">
                <FaEnvelope className="text-xl" />
                {unreadMessageCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-primary-pink text-white text-[10px] font-bold rounded-full ring-2 ring-[#0B0C15] flex items-center justify-center shadow-[0_0_8px_rgba(255,46,136,0.8)]">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </button>

              <div className="relative">
                <button className="text-text-secondary hover:text-primary-pink transition-colors relative p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)] notification-bell-button" onClick={() => { unlockAudio(); setNotificationOpen(!notificationOpen); }}>
                  <FaBell className="text-xl" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-primary-pink text-white text-[10px] font-bold rounded-full ring-2 ring-[#0B0C15] flex items-center justify-center shadow-[0_0_8px_rgba(255,46,136,0.8)]">
                      {displayCount}
                    </span>
                  )}
                </button>
                {notificationOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-glass-card backdrop-blur-xl rounded-xl shadow-[0_4px_30px_rgba(0,0,0,0.5)] border border-glass-border overflow-hidden z-50 notification-dropdown-container">
                    <div className="p-4 border-b border-glass-border font-bold text-text-primary flex justify-between items-center">
                      <span>Notifications</span>
                      <div className="flex gap-2">
                        {unreadCount > 0 && (
                          <button onClick={markAllAsRead} className="text-xs text-primary-pink hover:underline">Mark all read</button>
                        )}
                        {notifications.length > 0 && (
                          <button onClick={clearAllNotifications} className="text-xs text-red-500 hover:underline">Clear All</button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto custom-scrollbar">
                      {loadingNotifications ? (
                        <div className="p-8 text-center text-text-muted">Loading...</div>
                      ) : notifications.length === 0 ? (
                        <div className="p-8 text-center text-text-muted">No notifications</div>
                      ) : (
                        notifications.slice(0, 5).map((notif) => (
                          <div key={notif._id} className={`p-4 border-b border-glass-border last:border-0 cursor-pointer transition-colors flex justify-between items-start ${!notif.isRead ? 'bg-[rgba(255,46,136,0.1)]' : 'hover:bg-[rgba(255,255,255,0.05)]'}`} onClick={() => { if (!notif.isRead) markAsRead(notif._id); }}>
                            <div className="flex-1">
                              <p className="text-sm text-text-primary font-medium">{notif.message}</p>
                              <p className="text-xs text-text-muted mt-1">{new Date(notif.createdAt).toLocaleString()}</p>
                            </div>
                            {!notif.isRead && <FaCircle className="text-primary-pink text-xs mt-1 flex-shrink-0" />}
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-3 text-center border-t border-glass-border">
                        <button onClick={() => { setActiveTab("notifications"); setNotificationOpen(false); }} className="text-sm text-primary-pink font-bold hover:underline">View All ({notifications.length})</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="relative group hidden md:block">
                <button className="flex items-center space-x-3 focus:outline-none hover:bg-[rgba(255,255,255,0.05)] px-3 py-2 rounded-xl transition-colors" onClick={() => { unlockAudio(); setActiveTab("profile"); }}>
                  {profile ? (
                    <>
                      <img src={profile.profilePhoto || (profile.photos && profile.photos.length > 0 ? profile.photos[0] : null) || (profile.basicInfo?.gender === "Female" ? "https://randomuser.me/api/portraits/women/85.jpg" : "https://randomuser.me/api/portraits/men/85.jpg")} onError={(e) => { e.target.onerror = null; e.target.src = profile.basicInfo?.gender === "Female" ? "https://randomuser.me/api/portraits/women/85.jpg" : "https://randomuser.me/api/portraits/men/85.jpg"; }} alt="User" className="w-9 h-9 rounded-full border border-dark-navy shadow-[0_0_10px_rgba(255,255,255,0.1)] object-cover" />
                      <span className="font-semibold text-text-primary drop-shadow-sm">{profile.basicInfo?.firstName || "User"}</span>
                    </>
                  ) : (
                    <>
                      <div className="w-9 h-9 rounded-full bg-[rgba(255,255,255,0.05)] animate-pulse"></div>
                      <div className="w-16 h-4 bg-[rgba(255,255,255,0.05)] rounded animate-pulse"></div>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Content Scroll Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-transparent relative z-10 p-4 lg:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

// Updated Notifications Page with Mark as Read & Clear All
const NotificationsPage = ({ notifications, onClearAll, onMarkAllRead }) => {
  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FaBell className="text-rose-600" />
            Notifications
          </h2>
          <div className="flex gap-3">
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} className="px-5 py-2 bg-rose-100 text-rose-600 rounded-xl font-bold hover:bg-rose-200 transition-colors flex items-center gap-2">
                <FaCheckCircle /> Mark All Read
              </button>
            )}
            {notifications.length > 0 && (
              <button onClick={onClearAll} className="px-5 py-2 bg-red-100 text-red-600 rounded-xl font-bold hover:bg-red-200 transition-colors flex items-center gap-2">
                <FaTrash /> Clear All
              </button>
            )}
          </div>
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-20">
            <FaBell className="text-6xl text-text-secondary mx-auto mb-4" />
            <p className="text-xl text-text-muted">No notifications yet</p>
            <p className="text-text-muted mt-2">We'll let you know when something happens!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map(notif => (
              <div key={notif._id} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-lg font-medium text-gray-800">{notif.message}</p>
                  <p className="text-sm text-text-muted mt-2">{new Date(notif.createdAt).toLocaleString()}</p>
                </div>
                {!notif.isRead && (
                  <FaCircle className="text-rose-600 text-xs mt-1" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const SidebarItem = ({
  icon,
  label,
  active,
  onClick,
  badge,
  locked = false,
}) => (
  <button
    onClick={locked ? null : onClick}
    className={`group relative w-full flex items-center justify-between px-4 py-3 mb-2 rounded-xl transition-all duration-300 overflow-hidden
      ${locked
        ? "text-text-muted cursor-not-allowed bg-glass-card opacity-70"
        : active
          ? "bg-primary-pink/10 text-primary-pink dark:text-white font-medium shadow-[0_0_15px_rgba(255,46,136,0.2)] border border-primary-pink/30"
          : "text-text-secondary hover:bg-glass-card hover:text-text-primary dark:hover:text-white border border-transparent"}
    `}
  >
    {/* Active Glow Effect */}
    {active && (
      <div className="absolute inset-0 bg-gradient-to-r from-primary-pink/20 to-transparent opacity-50 pointer-events-none" />
    )}

    {/* Left accent bar for active */}
    {active && (
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary-pink rounded-r-full shadow-[0_0_10px_#FF2E88]" />
    )}

    <div className="flex items-center gap-3 relative z-10">
      <span className={`transition-transform duration-300 ${!locked && !active ? 'group-hover:scale-110 group-hover:text-primary-pink' : ''} ${active ? 'text-primary-pink' : ''}`}>
        {icon}
      </span>
      <span className={`flex items-center gap-2 text-sm tracking-wide ${active ? 'font-semibold' : ''}`}>
        {label}

        {/* 🔒 Premium Lock */}
        {locked && (
          <span className="flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest
            bg-rose-100 dark:bg-primary-grad-end/20 text-[#D54A45] dark:text-primary-grad-end border border-[#D54A45]/30 dark:border-primary-grad-end/40 backdrop-blur-md shadow-sm dark:shadow-[0_0_8px_rgba(123,97,255,0.3)]">
            Pro
            <LockIconLucide className="w-3 h-3" />
          </span>
        )}
      </span>
    </div>

    {/* Right side badge */}
    {!locked && badge && (
      <span className="relative z-10 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-gradient-to-r from-accent-cyan/80 dark:from-accent-cyan to-primary-grad-end/80 dark:to-primary-grad-end text-gray-900 dark:text-white shadow-sm dark:shadow-[0_0_10px_rgba(0,229,255,0.4)] border border-gray-900/10 dark:border-white/20">
        {badge}
      </span>
    )}
  </button>
);


export default Dashboard;